// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {YOUTUBE_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import styles from '@app/features/channel/components/embeds/media/EmbedYouTube.module.css';
import {OverlayActionButton, OverlayPlayButton} from '@app/features/channel/components/embeds/media/MediaButtons';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';
import {createCalculator} from '@app/features/ui/utils/DimensionUtils';
import type {MessageEmbed} from '@app/vendor/schema/domains/message/EmbedSchemas';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon, PlayIcon} from '@phosphor-icons/react';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {type FC, useCallback, useEffect, useState} from 'react';
import {thumbHashToDataURL} from 'thumbhash';

const YOUTUBE_CONFIG = {
	DEFAULT_WIDTH: 400,
	ANIMATION_DURATION: 0.3,
	BUTTON_DELAY: 0.1,
} as const;
const youtubeCalculator = createCalculator({
	maxWidth: YOUTUBE_CONFIG.DEFAULT_WIDTH,
	responsive: true,
});
interface EmbedYouTubeProps {
	embed: MessageEmbed;
	width?: number;
}
interface ThumbnailProps {
	posterSrc: string;
	thumbHashURL?: string;
	posterLoaded: boolean;
	title?: string;
	onPlay: (event: React.MouseEvent | React.KeyboardEvent) => void;
	onOpenInNewTab: (event: React.MouseEvent | React.KeyboardEvent) => void;
}
const Thumbnail: FC<ThumbnailProps> = observer(
	({posterSrc, thumbHashURL, posterLoaded, title, onPlay, onOpenInNewTab}) => {
		const {t} = useLingui();
		return (
			<div className={styles.thumbnail}>
				<AnimatePresence>
					{thumbHashURL && !posterLoaded && (
						<>
							<motion.img
								key="placeholder"
								initial={{opacity: 1}}
								exit={{opacity: 0}}
								transition={{duration: Accessibility.useReducedMotion ? 0 : 0.2}}
								src={thumbHashURL}
								alt={t`Video thumbnail`}
								className={styles.thumbnailPlaceholder}
							/>
							<motion.div
								key="overlay"
								initial={{opacity: 1}}
								exit={{opacity: 0}}
								transition={{duration: Accessibility.useReducedMotion ? 0 : 0.2}}
								className={styles.overlay}
							/>
						</>
					)}
				</AnimatePresence>
				<motion.img
					src={posterSrc}
					alt={title || 'Video thumbnail'}
					className={styles.posterImage}
					initial={{opacity: 0}}
					animate={{opacity: posterLoaded ? 1 : 0}}
					transition={{duration: Accessibility.useReducedMotion ? 0 : 0.2}}
				/>
				<div className={styles.controlsContainer}>
					<div className={styles.buttonGroup}>
						<OverlayPlayButton
							onClick={onPlay}
							icon={<PlayIcon size={28} aria-hidden="true" />}
							ariaLabel={t`Play video`}
						/>
						<OverlayActionButton
							onClick={onOpenInNewTab}
							icon={<ArrowSquareOutIcon size={24} aria-hidden="true" />}
							ariaLabel={t`Open in new tab`}
						/>
					</div>
				</div>
			</div>
		);
	},
);
export const EmbedYouTube: FC<EmbedYouTubeProps> = observer(({embed, width = YOUTUBE_CONFIG.DEFAULT_WIDTH}) => {
	const {t} = useLingui();
	const [hasInteracted, setHasInteracted] = useState(false);
	const posterSrc = embed.thumbnail?.proxy_url || '';
	const [posterLoaded, setPosterLoaded] = useState(() => ImageCacheUtils.hasImage(posterSrc));
	useEffect(() => {
		if (posterSrc) {
			if (ImageCacheUtils.hasImage(posterSrc)) {
				setPosterLoaded(true);
				return;
			}
			setPosterLoaded(false);
			ImageCacheUtils.loadImage(
				posterSrc,
				() => setPosterLoaded(true),
				() => setPosterLoaded(false),
			);
		}
	}, [posterSrc]);
	const handleInitialPlay = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		setHasInteracted(true);
	}, []);
	const handleOpenInNewTab = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			event.stopPropagation();
			if (embed.url) {
				openExternalUrlWithWarning(embed.url);
			}
		},
		[embed.url],
	);
	if (!(embed.video && embed.thumbnail && embed.thumbnail.proxy_url)) {
		return null;
	}
	const thumbHashUrl = embed.thumbnail.placeholder
		? thumbHashToDataURL(Uint8Array.from(atob(embed.thumbnail.placeholder), (c) => c.charCodeAt(0)))
		: undefined;
	const {style: containerStyle, dimensions} = youtubeCalculator.calculate(
		{
			width: embed.video.width!,
			height: embed.video.height!,
		},
		{maxWidth: width, forceScale: true},
	);
	const aspectRatio = `${dimensions.width} / ${dimensions.height}`;
	if (!hasInteracted) {
		return (
			<div
				className={styles.container}
				style={{
					...containerStyle,
					width: `${dimensions.width}px`,
					aspectRatio,
					maxWidth: '100%',
				}}
			>
				<Thumbnail
					posterSrc={posterSrc}
					thumbHashURL={thumbHashUrl}
					posterLoaded={posterLoaded}
					title={embed.title}
					onPlay={handleInitialPlay}
					onOpenInNewTab={handleOpenInNewTab}
				/>
			</div>
		);
	}
	const videoUrl = embed.video.url ?? embed.url;
	if (!videoUrl) return null;
	const embedVideoUrl = new URL(videoUrl);
	embedVideoUrl.searchParams.set('autoplay', '1');
	embedVideoUrl.searchParams.set('auto_play', '1');
	return (
		<div
			className={styles.videoContainer}
			style={{
				...containerStyle,
				width: `${dimensions.width}px`,
				aspectRatio,
				maxWidth: '100%',
			}}
		>
			{/* biome-ignore lint/a11y/useIframeTitle: project policy forbids the native title attribute (NoNativeTitleAttribute test); aria-label provides the accessible name */}
			<iframe
				allow="autoplay; fullscreen"
				allowFullScreen
				sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
				src={embedVideoUrl.toString()}
				className={styles.iframe}
				aria-label={embed.title || t`${YOUTUBE_PROVIDER_NAME} video`}
			/>
		</div>
	);
});
